// The end portal (DIM-3). PURE (ENG-1).
//
// A stronghold's portal room holds twelve `end_portal_frame` blocks ringing a 3×3
// pool, each facing inward. Feeding an eye of ender to a frame fills it; fill all
// twelve and the pool lights. That is the whole ceremony, and until now nothing
// in the world performed it — the End's dragon, gateways, cities and elytra were
// all reachable only by starting a world in that dimension.
//
// Everything here is geometry over an accessor, so the ring can be tested without
// a world and the host only has to supply "what is at this cell".

/** Half-width of the frame ring: frames sit 2 out from the centre. */
const RING = 2;

/** One frame of the ring: its offset from the pool centre and the way it looks. */
export interface RingSlot { dx: number; dz: number; facing: string }

/**
 * The twelve frames of a ring, relative to the pool centre. Each faces INWARD —
 * the eye side looks at the pool — which is what makes a rotated stronghold still
 * produce a coherent ring: rotating the piece rotates both the offsets and the
 * `facing` property through the same STR-3 table.
 */
export const FRAME_RING: readonly RingSlot[] = Object.freeze([
  ...[-1, 0, 1].map((dx) => ({ dx, dz: -RING, facing: 'south' })), // north edge, looking south
  ...[-1, 0, 1].map((dx) => ({ dx, dz: RING, facing: 'north' })),
  ...[-1, 0, 1].map((dz) => ({ dx: -RING, dz, facing: 'east' })),
  ...[-1, 0, 1].map((dz) => ({ dx: RING, dz, facing: 'west' })),
]);

/** The nine pool cells the portal fills, relative to the centre. */
export const POOL: readonly (readonly [number, number])[] = Object.freeze(
  [-1, 0, 1].flatMap((dz) => [-1, 0, 1].map((dx) => [dx, dz] as const)),
);

/** What the host must be able to tell the portal about the world. */
export interface PortalProbe {
  /** The frame at a cell — its facing and whether it holds an eye — or null. */
  frameAt(x: number, y: number, z: number): { facing: string; eye: boolean } | null;
  /** The state id of a frame with this facing + eye. */
  frameState(facing: string, eye: boolean): number;
  /** The lit end-portal state id. */
  portalState: number;
  set(x: number, y: number, z: number, state: number): void;
}

export interface Ring {
  /** Pool centre (the middle of the 3×3). */
  centre: readonly [number, number, number];
  /** The twelve frame cells, in FRAME_RING order. */
  frames: readonly (readonly [number, number, number])[];
  /** How many of them already hold an eye. */
  eyes: number;
}

/** The ring centred exactly here, or null. Eye state is irrelevant — a ring is a
 *  ring whether or not it has been fed. */
export function ringAt(probe: PortalProbe, cx: number, cy: number, cz: number): Ring | null {
  const frames: [number, number, number][] = [];
  let eyes = 0;
  for (const slot of FRAME_RING) {
    const x = cx + slot.dx, z = cz + slot.dz;
    const f = probe.frameAt(x, cy, z);
    if (!f || f.facing !== slot.facing) return null;
    if (f.eye) eyes++;
    frames.push([x, cy, z]);
  }
  return { centre: [cx, cy, cz], frames, eyes };
}

/**
 * The ring a frame at (x,y,z) belongs to. Every centre within the ring's own
 * reach is tried rather than deriving one from the frame's facing: it is two
 * dozen cheap lookups on a single right-click, and it cannot be fooled by two
 * rings built beside each other.
 */
export function ringNear(probe: PortalProbe, x: number, y: number, z: number): Ring | null {
  for (let dz = -RING; dz <= RING; dz++) {
    for (let dx = -RING; dx <= RING; dx++) {
      const ring = ringAt(probe, x + dx, y, z + dz);
      if (ring && ring.frames.some(([fx, , fz]) => fx === x && fz === z)) return ring;
    }
  }
  return null;
}

/** Light a complete ring: the 3×3 pool fills with end portal. */
export function lightPortal(probe: PortalProbe, ring: Ring): void {
  const [cx, cy, cz] = ring.centre;
  for (const [dx, dz] of POOL) probe.set(cx + dx, cy, cz + dz, probe.portalState);
}

export type EyeResult = 'none' | 'inserted' | 'lit';

/**
 * Feed one eye of ender to the frame at (x,y,z).
 *
 * `'none'` — not a frame, or it already holds an eye (the host must NOT consume
 * the eye). `'inserted'` — the frame took it. `'lit'` — that was the twelfth and
 * the portal is now open.
 */
export function insertEye(probe: PortalProbe, x: number, y: number, z: number): EyeResult {
  const frame = probe.frameAt(x, y, z);
  if (!frame || frame.eye) return 'none';
  probe.set(x, y, z, probe.frameState(frame.facing, true));
  const ring = ringNear(probe, x, y, z);
  if (!ring || ring.eyes < FRAME_RING.length) return 'inserted';
  lightPortal(probe, ring);
  return 'lit';
}

/** Compass points, clockwise from north (−Z). */
const COMPASS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'] as const;

/**
 * Which way an eye of ender pulls, as a compass word. North is −Z and east is +X
 * (the STR-3 convention), and the octant is rounded so "north-east" covers the
 * 45° around the diagonal rather than only the exact diagonal.
 */
export function compassBearing(dx: number, dz: number): string {
  const deg = (Math.atan2(dx, -dz) * 180) / Math.PI; // 0 = north, +90 = east
  const octant = ((Math.round(deg / 45) % 8) + 8) % 8;
  return COMPASS[octant]!;
}
